// TESSERAX — COURBE DE VITESSE DE CHUTE (gravité).
//
// La vitesse de chute suit une courbe EXPONENTIELLE (géométrique), comme les vrais
// Tetris : interval(L) = floorMs + (startMs − floorMs) · decay^(L−1). À CHAQUE niveau
// l'écart au plancher est multiplié par `decay` (< 1) → la chute accélère « de plus en
// plus », et surtout l'incrément reste PERCEPTIBLE à tous les niveaux (un palier
// géométrique ne s'aplatit pas comme un logarithme). C'est le reproche corrigé : l'ancienne
// courbe LOGARITHMIQUE (startMs / (1 + growth·ln L)) dépensait presque toute son
// accélération avant la Profondeur 4, puis ne bougeait quasiment plus — « la vitesse ne
// change plus ». La forme géométrique répartit l'accélération sur toute la run.
//
// Référence : le Tetris Guideline (Tetris Worlds) calcule le temps par rangée comme
// (0.8 − 0.007·(L−1))^(L−1) s — une décroissance géométrique qui file vers le « 20G »
// (chute instantanée) très vite. On reste plus DOUX (puits 3D en perspective, plus dur à
// lire qu'un puits plat) et on s'arrête à un PLANCHER doux `floorMs` : au-delà, la pression
// vient du resserrement du lock-delay (lockDelayMs) et de la montée d'instabilité
// (core/pressure), pas d'une gravité qui devient injouable. C'est un changement assumé par
// rapport à l'ancien « non plafonné » : une asymptote vers 0 finissait par sous-déborder en
// flottant (interval = 0) et rendait le placement impossible — le plancher garde la run jouable.
//
// Module PUR : ni THREE ni DOM, pas de Math.random — fonction du seul niveau (et du
// mode). main.ts multiplie le résultat par runSpeedMult (reliques + résonances ;
// >1 = plus lent). Le déterminisme du défi du jour est préservé.

import { LOCK_DELAY } from './constants';

export type FallMode = 'normal' | 'zen';

export interface FallCurve {
  startMs: number;   // intervalle au niveau 1 (decay^0 = 1)
  decay: number;     // facteur géométrique PAR niveau (0 < decay < 1) : plus PETIT = accélère plus fort
  floorMs: number;   // plancher doux (asymptote) : la vitesse ne dépasse jamais 1/floorMs
}

// normal/daily : géométrique, plancher doux à 90 ms — accélère franchement et continûment.
// Zen : la détente. Même départ (1100 ms) mais decay plus haut (descend plus DOUCEMENT) et
// plancher bien plus haut (360 ms) → pas de montée d'instabilité, relief() au lieu du game
// over : on ne veut pas que ça devienne frénétique. Toujours ≥ normal à niveau égal.
export const FALL_CURVES: Record<FallMode, FallCurve> = {
  // Plancher ABAISSÉ 90 → 55 ms : la chute restait « confortable » dès la Profondeur ~12
  // (le plancher était atteint trop tôt et trop haut) ; en profondeur la vitesse ne mordait
  // plus. Les premiers niveaux sont inchangés (le terme géométrique decay^(L−1) domine
  // jusqu'à ~Profondeur 12) — seul le tard de partie devient franchement plus rapide. On
  // reste au-dessus du « 20G » d'un Tetris plat : le puits 3D en perspective est plus dur à lire.
  normal: { startMs: 1100, decay: 0.80, floorMs: 55 },
  zen:    { startMs: 1100, decay: 0.86, floorMs: 360 },
};

// Intervalle de chute (ms) au niveau donné. Monotone décroissant jusqu'au plancher, qu'il
// approche en restant ≥ floorMs (jamais en dessous, jamais nul).
export function gravityMs(level: number, mode: FallMode = 'normal'): number {
  const c = FALL_CURVES[mode];
  // Garde NaN : un level non fini donnerait Math.pow(NaN) → intervalle NaN → `now-lastDrop > NaN`
  // toujours faux → la pièce ne tombe plus. On retombe sur le niveau 1 et on clampe au plancher.
  const L = Math.max(1, Number.isFinite(level) ? Math.floor(level) : 1);
  return Math.max(c.floorMs, c.floorMs + (c.startMs - c.floorMs) * Math.pow(c.decay, L - 1));
}

// ASSIST PREMIÈRES PARTIES — abaisse le plancher d'entrée. Les toutes premières runs d'un nouveau
// joueur (compteur cumulé `runs` en méta) reçoivent une chute plus DOUCE, qui s'estompe vite vers la
// normale (1.0) au bout de ~3 parties → le débutant a le temps d'apprivoiser le puits 3D, le joueur
// installé ne ressent rien. Renvoie un MULTIPLICATEUR d'intervalle ≥ 1 (>1 = plus lent), à appliquer
// comme runSpeedMult. PUR & déterministe — mais main.ts ne l'applique QU'aux modes NON seedés
// (libre/zen/strates), jamais en défi/rejeu : le déterminisme du défi du jour reste intact.
export const ASSIST_RAMP = [1.6, 1.35, 1.15];   // runs 0,1,2 ; au-delà → 1.0 (plus d'assist)
export function firstRunAssist(runs: number): number {
  const r = Number.isFinite(runs) ? Math.max(0, Math.floor(runs)) : 0;
  return r < ASSIST_RAMP.length ? ASSIST_RAMP[r] : 1.0;
}

// TESSERAX — FENÊTRE DE VERROUILLAGE (lock delay) selon la Profondeur.
//
// Jusqu'ici CONSTANTE (LOCK_DELAY = 520 ms à toute Profondeur). Couplée au hard-drop
// (instantané, gratuit) et à la file d'attente de 5 pièces, un joueur qui anticipe ne
// subissait JAMAIS la vitesse : il gardait tout le temps de réfléchir, donc « passer les
// premières Profondeurs » restait trivial — le seul axe de difficulté (la vitesse) était
// celui qu'on pouvait contourner. On RESSERRE doucement la fenêtre avec la Profondeur
// (même forme logarithmique douce que la gravité) pour que la vitesse FINISSE par mordre,
// jusqu'à un plancher sec (LOCK_FLOOR_MS) qui garde le placement jouable — jamais punitif.
// main.ts multiplie le résultat par lockDelayMult (reliques Amortisseur/Verre/Obsidienne).
// PUR & déterministe (fonction du seul niveau) : le défi du jour reste rejouable à l'identique.
// Plancher ABAISSÉ 280 → 200 ms et pente RELEVÉE 0,3 → 0,45 : la fenêtre se resserrait trop
// peu (≈260 ms en profondeur) pour que la chute accélérée se transforme en vraie pression. On
// la resserre plus vite et plus bas (≈200 ms en grande Profondeur) tout en gardant le placement
// jouable. Niveau 1 inchangé (log(1)=0) → onboarding intact.
export const LOCK_FLOOR_MS = 200;   // plancher : la fenêtre ne descend jamais en dessous
export const LOCK_GROWTH = 0.45;    // pente logarithmique du resserrement
export function lockDelayMs(level: number): number {
  const L = Math.max(1, Number.isFinite(level) ? Math.floor(level) : 1);   // garde NaN (cf. gravityMs)
  return Math.max(LOCK_FLOOR_MS, LOCK_DELAY / (1 + LOCK_GROWTH * Math.log(L)));
}
